import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';

import { useLazyQuery } from '#src/hooks/graphql/useLazyQuery.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { useLocalStorage } from '#src/hooks/useLocalStorage.ts';
import { isType } from '#src/lib/gql/index.ts';

import type {
  FullReferrerQuery,
  FullReferrerQueryVariables,
} from './__generated__/useEnterCode.graphql.ts';

type PromoCodeConfig = {
  code: string;
  referrer: string;
  utmCampaign: string;
};

const FULL_REFERRER_QUERY = gql`
  query FullReferrerQuery($referrerCode: String!) {
    fullReferrer(referrerCode: $referrerCode) {
      ... on User {
        slug
        nickname
      }
      ... on UserSource {
        id
        name
        displayName
      }
    }
  }
` as TypedDocumentNode<FullReferrerQuery, FullReferrerQueryVariables>;

export const useEnterCode = () => {
  const [error, setError] = useState<string | undefined>();
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
  const [, setSearchParams] = useSearchParams();

  const [storedReferrerCode, setReferrerCode] = useLocalStorage(
    'REFERRER_CODE',
    ''
  );
  const referrerCode = String(storedReferrerCode);
  const [, setReferrerIsAffiliate] = useLocalStorage(
    'REFERRER_IS_AFFILIATE',
    false
  );

  const [inputValue, setInputValueRaw] = useState(referrerCode);
  const resetInputValue = () => {
    setError(undefined);
    setInputValueRaw(referrerCode);
  };

  const { formatMessage } = useIntl();
  const {
    flags: { promoCodesConfig },
  } = useFeatureFlags();
  const [fetchReferrer] = useLazyQuery(FULL_REFERRER_QUERY);

  const promoCodes: PromoCodeConfig[] = promoCodesConfig?.promoCodes ?? [];

  const setInputValue = (val: string) => {
    setError(undefined);
    setInputValueRaw(val.replace(/\s/g, ''));
  };

  const validateAndApplyCode = async (): Promise<{
    valid: boolean;
    inviterName: string | null;
  }> => {
    const trimmed = inputValue.trim();
    if (!trimmed) return { valid: true, inviterName: null };

    const promoCodeConfig = promoCodes.find(p => p.code === trimmed);
    if (promoCodeConfig) {
      setReferrerCode(promoCodeConfig.code);
      setReferrerIsAffiliate(false);
      setSearchParams(
        prev => {
          prev.set('utm_campaign', promoCodeConfig.utmCampaign);
          prev.set('referrer_code', promoCodeConfig.code);
          prev.set('referrer', promoCodeConfig.referrer);
          prev.set('action', 'signup');
          return prev;
        },
        { replace: true }
      );
      return { valid: true, inviterName: null };
    }

    const { data } = await fetchReferrer({
      variables: { referrerCode: trimmed },
    });
    const { fullReferrer } = data || {};
    if (fullReferrer) {
      const referrerIsAffiliate = isType(fullReferrer, 'UserSource');
      setReferrerCode(trimmed);
      setReferrerIsAffiliate(referrerIsAffiliate);
      setSearchParams(
        prev => {
          prev.set('referrer_code', trimmed);
          prev.set('action', 'signup');
          return prev;
        },
        { replace: true }
      );
      return {
        valid: true,
        inviterName: isType(fullReferrer, 'User')
          ? fullReferrer.nickname
          : fullReferrer.displayName || fullReferrer.name,
      };
    }

    setError(
      formatMessage({
        defaultMessage: 'Sorry, this code is incorrect or expired',
      })
    );
    return { valid: false, inviterName: null };
  };

  return {
    inputValue,
    setInputValue,
    resetInputValue,
    error,
    successSnackbarOpen,
    setSuccessSnackbarOpen,
    validateAndApplyCode,
    alreadyApplied: !!referrerCode,
  };
};
